import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

const INACTIVITY_MS = 12 * 60 * 60 * 1000; // 12h de inatividade
const bumpActivity = () => localStorage.setItem("alpha_last_activity", Date.now().toString());

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, obj=auth
  const [stores, setStores] = useState([]);

  const loadStores = useCallback(async () => {
    try {
      const { data } = await api.get("/stores");
      setStores(data);
    } catch (e) { console.error("loadStores failed", e); }
  }, []);

  const logout = useCallback(async (opts = {}) => {
    try { await api.post("/auth/logout"); } catch (e) { console.error("logout request failed", e); }
    localStorage.removeItem("alpha_token");
    localStorage.removeItem("alpha_last_activity");
    setUser(false);
    if (opts.expired) toast.error("Sua sessão expirou por inatividade. Faça login novamente.");
  }, []);

  const bootstrap = useCallback(async () => {
    const token = localStorage.getItem("alpha_token");
    if (!token) { setUser(false); return; }
    const last = parseInt(localStorage.getItem("alpha_last_activity") || "0", 10);
    if (last && Date.now() - last > INACTIVITY_MS) {
      localStorage.removeItem("alpha_token");
      localStorage.removeItem("alpha_last_activity");
      setUser(false);
      toast.error("Sua sessão expirou por inatividade. Faça login novamente.");
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      bumpActivity();
      loadStores();
    } catch (e) {
      localStorage.removeItem("alpha_token");
      setUser(false);
    }
  }, [loadStores]);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  // Monitora inatividade enquanto logado
  useEffect(() => {
    if (!user) return;
    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, bumpActivity, { passive: true }));
    const iv = setInterval(() => {
      const last = parseInt(localStorage.getItem("alpha_last_activity") || "0", 10);
      if (last && Date.now() - last > INACTIVITY_MS) {
        clearInterval(iv);
        logout({ expired: true });
      }
    }, 60 * 1000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, bumpActivity));
      clearInterval(iv);
    };
  }, [user, logout]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("alpha_token", data.token);
      bumpActivity();
      setUser(data);
      loadStores();
      toast.success(`Bem-vindo, ${data.name.split(" ")[0]}!`);
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const switchStore = async (storeId) => {
    await api.post(`/users/switch-store/${storeId}`);
    setUser((u) => ({ ...u, active_store_id: storeId }));
    toast.success("Loja alterada");
  };

  const hasPerm = (perm) => {
    const perms = user?.effective_permissions || [];
    if (perms.includes("*") || perms.includes(perm)) return true;
    const mod = perm.split(".")[0];
    return perms.includes(`${mod}.*`);
  };

  return (
    <AuthContext.Provider value={{ user, stores, login, logout, switchStore, hasPerm, refreshStores: loadStores }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
